import type { DailyDataUsage, TerminalUsage } from '../services/clarusService';

/**
 * KAN-111: which days the usage chart draws.
 *
 * Clarus reports usage per billing cycle, and on the 1st of a month that cycle
 * is one day long — a chart of a single bar. A rolling 30-day window spans the
 * cycle boundary and always has something to show, so it is the default; the
 * billing cycle stays available because that is what the allowance is measured
 * against.
 *
 * The allowance figures (used, remaining, forecast) are always the CURRENT
 * cycle's regardless of which window the chart shows — a rolling window has no
 * allowance.
 */

export type UsageWindow = 'last30' | 'cycle';

export const dailyUsageOf = (usage: TerminalUsage | null | undefined): DailyDataUsage[] =>
  usage?.billingCycles?.[0]?.dailyDataUsage ?? [];

/**
 * Previous cycle's days followed by this cycle's, de-duplicated by date and
 * trimmed to the most recent `days`.
 *
 * De-duplication matters because the two payloads can overlap at the boundary,
 * and a duplicated date would draw two bars for one day.
 */
export const lastNDays = (
  previous: DailyDataUsage[],
  current: DailyDataUsage[],
  days = 30
): DailyDataUsage[] => {
  const byDate = new Map<string, DailyDataUsage>();
  for (const d of [...previous, ...current]) {
    if (d?.date) byDate.set(d.date, d);
  }
  return Array.from(byDate.values())
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(-days);
};

/**
 * The billing period immediately before the one given, as YYYY-MM.
 * Returns null for anything unparseable rather than guessing a month.
 */
export const previousBillingPeriod = (period: string | null | undefined): string | null => {
  if (!period) return null;
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  const d = month === 1 ? { y: year - 1, m: 12 } : { y: year, m: month - 1 };
  return `${d.y}-${String(d.m).padStart(2, '0')}`;
};

/**
 * Only worth a second API call when this cycle cannot fill the window on its
 * own. Mid-month, the current cycle already covers 30 days.
 */
export const needsPreviousCycle = (current: DailyDataUsage[], days = 30): boolean =>
  current.length < days;

/**
 * Some service lines report `subscriptionAllowance: 0` — typically a plan
 * activated the same day, or one billed purely on overage. Clarus then computes
 * `remainingAllowance` as `0 - used`, so the page showed "Remaining -1.92 GB".
 *
 * The figure is faithful to the API and meaningless to a reader: there is no
 * allowance to have any of left.
 */
export const hasAllowance = (usage: TerminalUsage | null | undefined): boolean =>
  !!usage?.subscriptionAllowance && usage.subscriptionAllowance > 0;

/** True when usage on a no-allowance plan is billed as overage. */
export const isOverageBilled = (usage: TerminalUsage | null | undefined): boolean =>
  !hasAllowance(usage) && usage?.servicePlan?.isOptedIntoOverage === true;
